/**
 * CustomizeColumnsPopover.
 *
 * Dropdown popover that opens from the Settings (gear) icon in the
 * RecurringToolbar. Shows a checkbox for each customizable column.
 * Required columns (Template name, Next date) are locked ON with a
 * "Required" chip so the user cannot accidentally break the page.
 *
 * Portal-mounted to document.body so it escapes any overflow:hidden
 * parents and sits above the sticky chrome. Closes on outside click
 * or Esc or clicking the Done button.
 *
 * Preferences aren't stored here; the parent passes `visible` and
 * `onToggle` from the useColumnPreferences hook, which handles the
 * localStorage persistence.
 */

import {
  useEffect,
  useMemo,
  useRef,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";
import {
  CUSTOMIZABLE_COLUMNS,
  REQUIRED_COLUMNS,
  type ColumnKey,
} from "../recurringColumns";

export interface CustomizeColumnsPopoverProps {
  isOpen: boolean;
  /** The button the popover anchors to; falls back to viewport center if null. */
  anchorEl: HTMLElement | null;
  visible: ReadonlySet<ColumnKey>;
  onToggle: (key: ColumnKey) => void;
  onReset: () => void;
  onClose: () => void;
}

export function CustomizeColumnsPopover({
  isOpen,
  anchorEl,
  visible,
  onToggle,
  onReset,
  onClose,
}: CustomizeColumnsPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close on Escape or outside click.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (anchorEl?.contains(target)) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    // Register after the current event finishes so the click that
    // opened the popover doesn't immediately close it.
    const t = window.setTimeout(() => {
      document.addEventListener("mousedown", onClick);
    }, 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
      document.removeEventListener("mousedown", onClick);
    };
  }, [isOpen, onClose, anchorEl]);

  // Position below the anchor, right-aligned to its right edge,
  // clamped inside the viewport.
  const popoverStyle = useMemo<CSSProperties>(() => {
    const base: CSSProperties = {
      position: "fixed",
      zIndex: 1600,
    };
    if (!anchorEl) {
      return {
        ...base,
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }
    const r = anchorEl.getBoundingClientRect();
    const width = 260;
    const top = r.bottom + 6;
    let left = r.right - width;
    if (left < 8) left = 8;
    if (left + width > window.innerWidth - 8) {
      left = window.innerWidth - width - 8;
    }
    return { ...base, top, left, width };
  }, [anchorEl]);

  if (!isOpen) return null;

  const node = (
    <div ref={popoverRef} style={popoverStyle}>
      <div style={cardStyle}>
        <div style={headStyle}>
          <div style={titleStyle}>Customize columns</div>
          <div style={subStyle}>Toggle what shows in the table</div>
        </div>

        <div style={listStyle}>
          {CUSTOMIZABLE_COLUMNS.map((col) => {
            const isChecked = visible.has(col.key);
            const isRequired = REQUIRED_COLUMNS.has(col.key);
            return (
              <button
                key={col.key}
                type="button"
                onClick={() => (isRequired ? undefined : onToggle(col.key))}
                disabled={isRequired}
                style={{
                  ...rowStyle,
                  ...(isRequired ? rowLockedStyle : {}),
                }}
              >
                <span
                  style={{
                    ...checkboxStyle,
                    ...(isChecked ? checkboxCheckedStyle : {}),
                    ...(isRequired ? checkboxLockedStyle : {}),
                  }}
                >
                  {isChecked && <Check size={11} strokeWidth={3} />}
                </span>
                <span style={labelStyle}>{col.label}</span>
                {isRequired && <span style={lockChipStyle}>Required</span>}
              </button>
            );
          })}
        </div>

        <div style={footStyle}>
          <button type="button" onClick={onReset} style={resetStyle}>
            Reset to defaults
          </button>
          <button type="button" onClick={onClose} style={doneBtnStyle}>
            Done
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}

// ---------- Styles ----------

const cardStyle: CSSProperties = {
  background: "#ffffff",
  border: "1px solid #D8DEE6",
  borderRadius: 10,
  boxShadow: "0 12px 32px -4px rgba(14, 26, 26, 0.18)",
  overflow: "hidden",
};

const headStyle: CSSProperties = {
  padding: "12px 14px 10px",
  borderBottom: "1px solid #E7EAF0",
};

const titleStyle: CSSProperties = {
  fontSize: 11,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  fontWeight: 700,
  color: "#3A4A52",
};

const subStyle: CSSProperties = {
  fontSize: 11.5,
  color: "#6B7684",
  marginTop: 2,
};

const listStyle: CSSProperties = {
  padding: "4px 0",
  maxHeight: 320,
  overflowY: "auto",
};

const rowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 14px",
  cursor: "pointer",
  fontSize: 13,
  color: "#0E1A1A",
  background: "transparent",
  border: "none",
  width: "100%",
  textAlign: "left",
  fontFamily: "inherit",
};

const rowLockedStyle: CSSProperties = {
  color: "#6B7684",
  cursor: "not-allowed",
};

const checkboxStyle: CSSProperties = {
  width: 16,
  height: 16,
  border: "1.5px solid #D8DEE6",
  borderRadius: 4,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  background: "#ffffff",
  color: "transparent",
};

const checkboxCheckedStyle: CSSProperties = {
  background: "#0E1A1A",
  borderColor: "#0E1A1A",
  color: "#ffffff",
};

const checkboxLockedStyle: CSSProperties = {
  background: "#F3F5F5",
  borderColor: "#D8DEE6",
  color: "#6B7684",
};

const labelStyle: CSSProperties = {
  flex: 1,
};

const lockChipStyle: CSSProperties = {
  fontSize: 10,
  color: "#6B7684",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  fontWeight: 600,
};

const footStyle: CSSProperties = {
  padding: "10px 14px",
  borderTop: "1px solid #E7EAF0",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const resetStyle: CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#3A4A52",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 500,
  textDecoration: "underline",
  textUnderlineOffset: 2,
  padding: 0,
  fontFamily: "inherit",
};

const doneBtnStyle: CSSProperties = {
  background: "#0E1A1A",
  color: "#ffffff",
  padding: "6px 14px",
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  border: "none",
  fontFamily: "inherit",
};

export default CustomizeColumnsPopover;